/** Thin utility that reads vfx.json and returns typed config objects for VFX primitives. */
import Phaser from 'phaser';
import type { VfxPrimitiveConfig } from './primitives/VfxPrimitive';
import vfxData from '../data/tuning/vfx.json';

interface VfxEntry {
  primitive: string;
  [key: string]: unknown;
}

const BLEND_MODE_MAP: Record<string, Phaser.BlendModes> = {
  NORMAL: Phaser.BlendModes.NORMAL,
  ADD: Phaser.BlendModes.ADD,
  MULTIPLY: Phaser.BlendModes.MULTIPLY,
  SCREEN: Phaser.BlendModes.SCREEN,
};

export class VfxConfigLoader {
  private static entries: Map<string, VfxEntry> = new Map();
  private static defaults: Record<string, Record<string, unknown>> = {};
  private static loaded = false;

  /** Load and validate vfx.json. Called once during scene preload. */
  static load(): void {
    if (VfxConfigLoader.loaded) return;
    const data = vfxData as Record<string, unknown>;

    // Cache primitive defaults keyed by primitive class name
    const primitiveDefaults = data.primitiveDefaults as Record<string, Record<string, unknown>> | undefined;
    if (primitiveDefaults) {
      for (const [name, defs] of Object.entries(primitiveDefaults)) {
        VfxConfigLoader.defaults[name] = defs;
      }
    }

    const effects = data.effects as Record<string, VfxEntry> | undefined;
    if (effects) {
      for (const [key, entry] of Object.entries(effects)) {
        VfxConfigLoader.entries.set(key, entry);
      }
    }
    VfxConfigLoader.loaded = true;
  }

  /** Get typed config for a specific effect key. Merges primitiveDefaults, then parses hex tints and blend mode strings. */
  static get<T extends VfxPrimitiveConfig>(key: string): T {
    const entry = VfxConfigLoader.entries.get(key);
    if (!entry) throw new Error(`VFX config not found: ${key}`);

    // Merge: primitiveDefaults for this primitive type, then effect-specific overrides
    const defs = VfxConfigLoader.defaults[entry.primitive] ?? {};
    const config = { ...defs, ...entry } as Record<string, unknown>;

    // Parse hex tint string to number
    if (typeof config.tint === 'string') {
      config.tint = parseInt(config.tint as string, 16);
    }
    // Parse blend mode string to Phaser enum
    if (typeof config.blendMode === 'string') {
      config.blendMode = BLEND_MODE_MAP[config.blendMode as string] ?? Phaser.BlendModes.NORMAL;
    }
    return config as T;
  }

  /** Check if an entry exists. */
  static has(key: string): boolean {
    return VfxConfigLoader.entries.has(key);
  }

  /** Get the primitive class name for an entry. */
  static getPrimitiveType(key: string): string {
    const entry = VfxConfigLoader.entries.get(key);
    if (!entry) throw new Error(`VFX config not found: ${key}`);
    return entry.primitive;
  }
}
